import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, Trash2, X } from 'lucide-react';
import {
  ApiError,
  createSegment,
  deleteSegment,
  getSegments,
  qk,
  type SegmentConditions,
} from '../api.js';
import {
  SEGMENT_DIMENSIONS,
  SEGMENT_FLAG_DIMENSIONS,
  SEGMENT_MAX_CONDITIONS,
  type SegmentDimension,
} from '@dxp/schema';
import { roleAtLeast, type Role } from '../lib/auth.js';

/**
 * §92.4 — TEK segment seçici: Funnel ve Oturumlar aynı bileşeni kullanır
 * (Faz 7 dersi: kopya = ayrışma). İki grup çizer:
 *   · "Hazır" — sayfanın kendi kırılımı (Funnel'ın cihaz/kanal tekilleri;
 *     Oturumlar vermez, grup hiç görünmez),
 *   · "Kayıtlı segmentler" — §92.3 listesi + satır-içi oluşturma formu.
 *
 * Oluşturma MODAL DEĞİL satır-içidir (§92.4): sayfa terk edilmez. Kaydeden
 * kullanıcı sonucu hemen görsün diye yeni segment SEÇİLİ döner.
 *
 * Yetki UI'da da söylenir ama KARAR sunucudadır: viewer'da "Yeni segment" ve
 * silme düğmeleri hiç çizilmez (ROUTE_RULES editor ister).
 */

export type SegmentSecim =
  | { tur: 'tumu' }
  | { tur: 'hazir'; dimension: 'device' | 'channel'; value: string }
  | { tur: 'kayitli'; id: string };

/** Boyutların panel etiketleri — katalogla birlikte genişler (derleme zorlar). */
const BOYUT_ETIKETLERI: Record<SegmentDimension, string> = {
  device: 'Cihaz',
  browser: 'Tarayıcı',
  os: 'İşletim sistemi',
  channel: 'Kanal',
  utm_source: 'UTM kaynağı',
  converted: 'Dönüşüm',
  had_js_error: 'JS hatası',
  had_rage_click: 'Rage click',
};

const FLAGS = SEGMENT_FLAG_DIMENSIONS as readonly string[];

interface KosulSatiri {
  dim: SegmentDimension;
  deger: string;
}

/** Form satırlarını §92.2 `conditions` biçimine çevirir; boş satır atlanmaz, hata sayılır. */
function kosullariDerle(satirlar: KosulSatiri[]): SegmentConditions | null {
  const all: SegmentConditions['all'] = [];
  for (const s of satirlar) {
    const degerler = s.deger
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '');
    if (degerler.length === 0) return null;
    all.push({
      dim: s.dim,
      op: degerler.length > 1 ? 'in' : 'eq',
      values: degerler,
    });
  }
  return all.length === 0 ? null : { all };
}

function hataMetni(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'duplicate_name') return 'Bu adda bir segment zaten var.';
    if (err.code === 'segment_cap') return 'Segment tavanına ulaşıldı — önce birini silin.';
    if (err.code === 'invalid_segment') return 'Koşullar geçersiz — değerleri kontrol edin.';
    if (err.code === 'invalid_name') return 'Segment adı 1–60 karakter olmalı.';
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

export default function SegmentSecici({
  pid,
  role,
  secim,
  onChange,
  hazirSecenekler = [],
}: {
  pid: string;
  /** Projedeki rol — yazma düğmeleri `editor+`da görünür (karar sunucuda). */
  role: Role | null;
  secim: SegmentSecim;
  onChange: (secim: SegmentSecim) => void;
  /** Funnel'ın cihaz/kanal tekilleri; Oturumlar boş bırakır. */
  hazirSecenekler?: { dimension: 'device' | 'channel'; value: string }[];
}) {
  const [acik, setAcik] = useState(false);
  const [formAcik, setFormAcik] = useState(false);
  const [ad, setAd] = useState('');
  const [satirlar, setSatirlar] = useState<KosulSatiri[]>([{ dim: 'device', deger: '' }]);
  const kok = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const yazabilir = roleAtLeast(role, 'editor');

  // Dışarı tıklayınca kapat (FilterPill deseni).
  useEffect(() => {
    if (!acik) return;
    const dinle = (e: MouseEvent) => {
      if (kok.current !== null && !kok.current.contains(e.target as Node)) setAcik(false);
    };
    document.addEventListener('mousedown', dinle);
    return () => document.removeEventListener('mousedown', dinle);
  }, [acik]);

  const liste = useQuery({
    queryKey: qk.segments(pid),
    queryFn: ({ signal }) => getSegments(pid, signal),
  });
  const kayitlilar = liste.data?.items ?? [];

  const olustur = useMutation({
    mutationFn: () => {
      const conditions = kosullariDerle(satirlar);
      if (conditions === null) {
        return Promise.reject(new Error('Her koşula en az bir değer yazın.'));
      }
      return createSegment(pid, ad.trim(), conditions);
    },
    onSuccess: async (yeni) => {
      await queryClient.invalidateQueries({ queryKey: qk.segments(pid) });
      setFormAcik(false);
      setAd('');
      setSatirlar([{ dim: 'device', deger: '' }]);
      // §92.4 — kaydeden sonucu hemen görür.
      onChange({ tur: 'kayitli', id: yeni.id });
      setAcik(false);
    },
  });

  const sil = useMutation({
    mutationFn: (id: string) => deleteSegment(pid, id),
    onSuccess: async (_veri, id) => {
      await queryClient.invalidateQueries({ queryKey: qk.segments(pid) });
      // Silinen seçiliyse "Tümü"ne düş (§92.3).
      if (secim.tur === 'kayitli' && secim.id === id) onChange({ tur: 'tumu' });
    },
  });

  const etiket =
    secim.tur === 'tumu'
      ? 'Tüm oturumlar'
      : secim.tur === 'hazir'
        ? `${secim.dimension === 'device' ? 'Cihaz' : 'Kanal'}: ${secim.value}`
        : (kayitlilar.find((s) => s.id === secim.id)?.name ?? 'Segment');

  const satirSinifi =
    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-[#374151] hover:bg-[#f9fafb]';

  return (
    <div ref={kok} className="relative" data-testid="segment-secici">
      <button
        type="button"
        data-testid="segment-secici-dugme"
        onClick={() => setAcik((v) => !v)}
        className="flex items-center gap-1.5 rounded-full border border-[#e5e7eb] bg-white px-3 py-1.5 text-[12px] text-[#374151] transition-colors hover:border-[#c8c8c8]"
      >
        Segment: {etiket}
        <ChevronDown className="h-3.5 w-3.5 text-[#9ca3af]" />
      </button>

      {acik ? (
        <div className="absolute left-0 top-full z-40 mt-1.5 w-[300px] rounded-lg border border-[#e5e7eb] bg-white py-1.5 shadow-lg">
          <button
            type="button"
            className={satirSinifi}
            onClick={() => {
              onChange({ tur: 'tumu' });
              setAcik(false);
            }}
          >
            Tüm oturumlar
          </button>

          {hazirSecenekler.length > 0 ? (
            <>
              <p className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-wide text-[#9ca3af]">
                Hazır
              </p>
              {hazirSecenekler.map((h) => (
                <button
                  key={`${h.dimension}:${h.value}`}
                  type="button"
                  className={satirSinifi}
                  onClick={() => {
                    onChange({ tur: 'hazir', dimension: h.dimension, value: h.value });
                    setAcik(false);
                  }}
                >
                  {h.dimension === 'device' ? 'Cihaz' : 'Kanal'}: {h.value}
                </button>
              ))}
            </>
          ) : null}

          <p className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-wide text-[#9ca3af]">
            Kayıtlı segmentler
          </p>
          {kayitlilar.length === 0 ? (
            <p className="px-3 py-1 text-[12px] text-[#9ca3af]">Henüz kayıtlı segment yok.</p>
          ) : (
            kayitlilar.map((s) => (
              <div key={s.id} className="flex items-center">
                <button
                  type="button"
                  data-testid="segment-secenek"
                  className={`${satirSinifi} flex-1`}
                  onClick={() => {
                    onChange({ tur: 'kayitli', id: s.id });
                    setAcik(false);
                  }}
                >
                  {s.name}
                </button>
                {yazabilir ? (
                  <button
                    type="button"
                    aria-label={`${s.name} segmentini sil`}
                    data-testid={`segment-sil-${s.name}`}
                    onClick={() => sil.mutate(s.id)}
                    className="mr-2 shrink-0 rounded p-1 text-[#9ca3af] hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            ))
          )}

          {yazabilir ? (
            formAcik ? (
              <div className="border-t border-[#f3f4f6] px-3 py-2" data-testid="segment-form">
                <div className="mb-1.5 flex items-center justify-between">
                  <p className="text-[11px] font-medium text-[#111827]">Yeni segment</p>
                  <button
                    type="button"
                    aria-label="Formu kapat"
                    onClick={() => setFormAcik(false)}
                    className="rounded p-0.5 text-[#9ca3af] hover:text-[#374151]"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <input
                  data-testid="segment-ad"
                  value={ad}
                  onChange={(e) => setAd(e.target.value)}
                  placeholder="Ad (örn. Mobil + Organik)"
                  maxLength={60}
                  className="mb-1.5 h-8 w-full rounded-md border border-[#e5e7eb] px-2 text-[12px] outline-none focus:border-brand"
                />
                {satirlar.map((satir, i) => (
                  <div key={i} className="mb-1.5 flex items-center gap-1.5">
                    <select
                      data-testid={`segment-boyut-${i}`}
                      value={satir.dim}
                      onChange={(e) =>
                        setSatirlar((eski) =>
                          eski.map((s, j) =>
                            j === i ? { dim: e.target.value as SegmentDimension, deger: '' } : s,
                          ),
                        )
                      }
                      className="h-8 shrink-0 rounded-md border border-[#e5e7eb] bg-white px-1.5 text-[12px] outline-none focus:border-brand"
                    >
                      {SEGMENT_DIMENSIONS.map((d) => (
                        <option key={d} value={d}>
                          {BOYUT_ETIKETLERI[d]}
                        </option>
                      ))}
                    </select>
                    {FLAGS.includes(satir.dim) ? (
                      <select
                        data-testid={`segment-deger-${i}`}
                        value={satir.deger}
                        onChange={(e) =>
                          setSatirlar((eski) =>
                            eski.map((s, j) => (j === i ? { ...s, deger: e.target.value } : s)),
                          )
                        }
                        className="h-8 flex-1 rounded-md border border-[#e5e7eb] bg-white px-1.5 text-[12px] outline-none focus:border-brand"
                      >
                        <option value="">seçin…</option>
                        <option value="1">evet</option>
                        <option value="0">hayır</option>
                      </select>
                    ) : (
                      <input
                        data-testid={`segment-deger-${i}`}
                        value={satir.deger}
                        onChange={(e) =>
                          setSatirlar((eski) =>
                            eski.map((s, j) => (j === i ? { ...s, deger: e.target.value } : s)),
                          )
                        }
                        placeholder="mobile veya mobile,tablet"
                        className="h-8 min-w-0 flex-1 rounded-md border border-[#e5e7eb] px-2 text-[12px] outline-none focus:border-brand"
                      />
                    )}
                    {satirlar.length > 1 ? (
                      <button
                        type="button"
                        aria-label="Koşulu kaldır"
                        onClick={() => setSatirlar((eski) => eski.filter((_, j) => j !== i))}
                        className="shrink-0 rounded p-1 text-[#9ca3af] hover:text-red-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                ))}
                {satirlar.length < SEGMENT_MAX_CONDITIONS ? (
                  <button
                    type="button"
                    onClick={() => setSatirlar((eski) => [...eski, { dim: 'channel', deger: '' }])}
                    className="mb-1.5 flex items-center gap-1 text-[11px] text-brand hover:underline"
                  >
                    <Plus className="h-3 w-3" /> koşul ekle
                  </button>
                ) : null}
                {olustur.isError ? (
                  <p role="alert" data-testid="segment-hata" className="mb-1.5 text-[11px] text-red-600">
                    {hataMetni(olustur.error)}
                  </p>
                ) : null}
                <button
                  type="button"
                  data-testid="segment-kaydet"
                  disabled={ad.trim() === '' || olustur.isPending}
                  onClick={() => olustur.mutate()}
                  className="h-8 w-full rounded-md bg-brand text-[12px] font-medium text-white transition-colors hover:bg-[#d95b00] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {olustur.isPending ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="segment-yeni"
                onClick={() => setFormAcik(true)}
                className={`${satirSinifi} border-t border-[#f3f4f6] text-brand`}
              >
                <Plus className="h-3.5 w-3.5" /> Yeni segment
              </button>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
